import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { recordCache } from "./performance";
import { useDashboardRefresh } from "./dashboard-refresh";
import { replaceEqualDeep } from "./structural-sharing";

type Snapshot<T> = { data: T | undefined; error: unknown; fetchedAt: number; fetching: boolean; revision: number };
type Entry = {
  snapshot: Snapshot<unknown>;
  pending?: Promise<void>;
  controller?: AbortController;
  version: number;
  failures: number;
  retryAt: number;
  terminal: boolean;
  /** Set by invalidation: the next read is a full refresh, not a poll. */
  forceRefresh: boolean;
  /** When the in-flight read started as a refresh (concurrent forced
   * read-throughs from one manual refresh join it instead of racing). */
  refreshStartedAt: number;
};
/** Why a fetcher runs. "refresh" is a manual refresh, a mutation's reload or an
 * invalidation after a write: a new read boundary. "load" is a first read, a
 * stale-time poll, a focus or reconnect revalidation. "continue" extends the
 * cached data (a paged fetcher reads its next pages). Paged fetchers use it to
 * poll the first page only (P-08). */
export type DashboardFetchReason = "load" | "refresh" | "continue";
export type DashboardFetchContext = { reason: DashboardFetchReason };
export type DashboardFetcher<T> = (signal: AbortSignal, context: DashboardFetchContext) => Promise<T>;
const EMPTY: Snapshot<never> = { data: undefined, error: null, fetchedAt: 0, fetching: false, revision: 0 };
const cache = new Map<string, Entry>();
const listeners = new Map<string, Set<() => void>>();
const clearHooks = new Set<() => void>();
let cacheOwner: string | null = null;
const DEFAULT_STALE_MS = 60_000;
const MAX_ENTRIES = 250;
/** A focus refetch (refreshOnFocus) never fires more often than this (ACC-7). */
export const FOCUS_REFRESH_MIN_MS = 15_000;
/** Forced read-throughs started this close together share one request. */
const FORCED_READ_JOIN_MS = 2_000;

function emit(key: string) { listeners.get(key)?.forEach((listener) => listener()); }
function entryFor(key: string): Entry {
  let entry = cache.get(key);
  if (!entry) {
    // Evict only inactive entries, never a mounted reader mid-edit.
    if (cache.size >= MAX_ENTRIES) {
      for (const [oldKey, old] of cache) {
        if (!listeners.get(oldKey)?.size) { old.controller?.abort(); cache.delete(oldKey); }
        if (cache.size < MAX_ENTRIES) break;
      }
    }
    entry = { snapshot: EMPTY, version: 0, failures: 0, retryAt: 0, terminal: false, forceRefresh: false, refreshStartedAt: 0 };
    cache.set(key, entry);
  }
  return entry;
}
function publish(key: string, entry: Entry, patch: Partial<Snapshot<unknown>>) {
  entry.snapshot = { ...entry.snapshot, ...patch };
  emit(key);
}
/** Other account-scoped in-memory caches (console prefetches, model bytes)
 * register here so a sign-out or account switch clears them with this one. */
export function onDashboardResourceCacheCleared(hook: () => void): () => void {
  clearHooks.add(hook);
  return () => clearHooks.delete(hook);
}
export function clearDashboardResourceCache(): void {
  for (const entry of cache.values()) entry.controller?.abort();
  cache.clear();
  cacheOwner = null;
  for (const hook of clearHooks) hook();
  listeners.forEach((_, key) => emit(key));
}
export function resetDashboardResourceCache(ownerId: string | null): void {
  if (cacheOwner === ownerId) return;
  clearDashboardResourceCache();
  cacheOwner = ownerId;
}
export function peekDashboardResource<T>(key: string): T | undefined {
  return cache.get(key)?.snapshot.data as T | undefined;
}
/** Changes whenever a new read or invalidation supersedes cached data. */
export function peekDashboardResourceVersion(key: string): number | undefined {
  return cache.get(key)?.version;
}
export function peekDashboardResourceFetchedAt(key: string): number {
  return cache.get(key)?.snapshot.fetchedAt ?? 0;
}
export function writeDashboardResource<T>(key: string, data: T, options: { fetchedAt?: number } = {}): void {
  const entry = entryFor(key);
  entry.version++;
  entry.controller?.abort();
  entry.pending = undefined;
  entry.failures = 0; entry.retryAt = 0; entry.terminal = false; entry.forceRefresh = false;
  publish(key, entry, { data: replaceEqualDeep(entry.snapshot.data, data), error: null, fetchedAt: options.fetchedAt ?? Date.now(), fetching: false });
}
/** Fills an empty entry from an identical read held under another key (the
 * work queue's first page from Home's, P-07). Never overwrites, never emits:
 * a reader that subscribes afterwards starts from this snapshot and the
 * normal stale-time rules decide whether it revalidates. */
export function seedDashboardResource<T>(key: string, data: T, fetchedAt: number): boolean {
  const existing = cache.get(key);
  if (existing && (existing.snapshot.data !== undefined || existing.pending)) return false;
  const entry = entryFor(key);
  entry.snapshot = { ...entry.snapshot, data, error: null, fetchedAt };
  return true;
}
/** Late pre-mutation reads cannot overwrite a successful mutation's result. */
export function invalidateDashboardResources(matches: (key: string) => boolean): void {
  for (const [key, entry] of cache) {
    if (!matches(key)) continue;
    entry.version++;
    entry.controller?.abort(); entry.pending = undefined;
    entry.retryAt = 0; entry.failures = 0; entry.terminal = false; entry.forceRefresh = true;
    publish(key, entry, { fetchedAt: 0, fetching: false, revision: entry.snapshot.revision + 1 });
  }
}

function loadResource<T>(key: string, fetcher: DashboardFetcher<T>, requested: DashboardFetchReason = "load"): Promise<void> {
  const entry = entryFor(key);
  if (entry.pending) { recordCache(true); return entry.pending; }
  recordCache(false);
  const reason: DashboardFetchReason = entry.forceRefresh ? "refresh" : requested;
  entry.forceRefresh = false;
  entry.refreshStartedAt = reason === "refresh" ? Date.now() : 0;
  const version = ++entry.version;
  const controller = new AbortController();
  entry.controller = controller;
  const current = () => cache.get(key) === entry && entry.version === version && !controller.signal.aborted;
  // One publish per settled read: data (structurally shared with the previous
  // value, so an unchanged poll keeps every object identity) and fetching:false
  // land together in the finally below.
  let settled: Partial<Snapshot<unknown>> = {};
  // Install the promise before notifying readers. Concurrent mounts and
  // refreshes join the same request, including StrictMode effect replay.
  entry.pending = Promise.resolve().then(() => {
    if (!current()) throw new DOMException("Aborted", "AbortError");
    return fetcher(controller.signal, { reason });
  }).then((data) => {
    if (!current()) return;
    entry.failures = 0; entry.retryAt = 0; entry.terminal = false;
    // A continuation adds later pages; the data's age (what the next poll is
    // scheduled from) is still that of the first page it extends.
    const fetchedAt = reason === "continue" && entry.snapshot.fetchedAt > 0 ? entry.snapshot.fetchedAt : Date.now();
    settled = { data: replaceEqualDeep(entry.snapshot.data, data), error: null, fetchedAt };
  }).catch((error: unknown) => {
    if (!current()) return;
    const status = (error as { status?: number } | null)?.status;
    entry.terminal = status === 401 || status === 403 || status === 404;
    entry.failures++;
    const serverDelay = (error as { retryAfterMs?: number } | null)?.retryAfterMs ?? 0;
    entry.retryAt = Date.now() + Math.max(serverDelay, Math.min(300_000, 5_000 * 2 ** Math.min(entry.failures - 1, 6)) * (1 + Math.random() * 0.2));
    settled = { error, ...(entry.terminal ? { data: undefined } : {}) };
  }).finally(() => {
    if (cache.get(key) !== entry || entry.version !== version) return;
    entry.pending = undefined;
    publish(key, entry, { ...settled, fetching: false });
  });
  publish(key, entry, { error: null, fetching: true });
  return entry.pending;
}

/** Extends cached data through the entry's normal read path (reason
 * "continue"): joins an in-flight read, is aborted with the last reader and
 * records failures with the usual backoff. */
export function continueDashboardResource<T>(key: string, fetcher: DashboardFetcher<T>): Promise<void> {
  const entry = cache.get(key);
  if (!entry || entry.snapshot.data === undefined) return Promise.resolve();
  if (entry.pending) return entry.pending;
  return loadResource(key, fetcher, "continue");
}

function fresh(entry: Entry | undefined, staleTimeMs: number): boolean {
  return Boolean(entry && entry.snapshot.data !== undefined && !entry.snapshot.error && entry.snapshot.fetchedAt > 0
    && Date.now() - entry.snapshot.fetchedAt < staleTimeMs);
}

/** Starts a read nobody renders yet (the console bootstrap, P-01). Fresh or
 * in-flight entries are left alone; failures stay in the entry for the reader. */
export function prefetchDashboardResource<T>(key: string, fetcher: DashboardFetcher<T>, staleTimeMs = DEFAULT_STALE_MS): Promise<void> {
  const entry = cache.get(key);
  if (entry?.pending) return entry.pending;
  if (fresh(entry, staleTimeMs)) return Promise.resolve();
  return loadResource(key, fetcher);
}

function abortable<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", abort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}

/**
 * Read-through for one shared resource used inside several panels' composite
 * reads (P-14, SHOP-2, ACC-2). Fresh data is returned without a request, an
 * in-flight read is joined, otherwise one read runs and is cached under `key`
 * for every other caller. `force` (a manual refresh or a post-write reload of
 * the caller) always reads again. Errors are thrown to the caller.
 *
 * Forced reads started within FORCED_READ_JOIN_MS of each other share one
 * request, which is right for several panels refreshing together. A caller
 * that has just WRITTEN (`supersede`) must never share a read that started
 * before its write: that read can still return the pre-write row (ONB-10).
 */
export async function readDashboardResource<T>(key: string, fetcher: DashboardFetcher<T>, options: {
  staleTimeMs?: number; force?: boolean; signal?: AbortSignal; supersede?: boolean;
} = {}): Promise<T> {
  const staleTimeMs = options.staleTimeMs ?? DEFAULT_STALE_MS;
  let entry = cache.get(key);
  if (!options.force && fresh(entry, staleTimeMs) && !entry!.forceRefresh) return entry!.snapshot.data as T;
  const joinsRefresh = Boolean(options.force && !options.supersede && entry?.pending && Date.now() - entry.refreshStartedAt < FORCED_READ_JOIN_MS);
  if (options.force && entry && !joinsRefresh) {
    // Same boundary as a hook refresh: an in-flight read may predate the
    // caller's write, so it is superseded rather than joined.
    entry.version++;
    entry.controller?.abort(); entry.pending = undefined;
    entry.terminal = false; entry.retryAt = 0;
  }
  await abortable(loadResource(key, fetcher, options.force ? "refresh" : "load"), options.signal);
  for (;;) {
    // A read superseded while awaited never published; report the newest one.
    while (cache.get(key)?.pending) await abortable(cache.get(key)!.pending!, options.signal);
    entry = cache.get(key);
    if (!entry) throw new DOMException("Aborted", "AbortError");
    // A write invalidated the entry while this read was in flight: the read was
    // aborted and nothing has published since, so the snapshot predates the
    // write. Read again instead of returning it. (The identity-verification
    // start POST invalidates the profile under the wizard's forced poll; the
    // stale profile read as "no session started" and let a second start
    // through.) Each pass needs another write to land mid-read, and the
    // caller's signal still ends the wait.
    if (!entry.forceRefresh) break;
    await abortable(loadResource(key, fetcher, "refresh"), options.signal);
  }
  if (entry.snapshot.error) throw entry.snapshot.error;
  if (entry.snapshot.data === undefined) throw new DOMException("Aborted", "AbortError");
  return entry.snapshot.data as T;
}

export type DashboardResource<T> = {
  data: T | undefined;
  loading: boolean;
  refreshing: boolean;
  error: unknown;
  refresh: () => Promise<void>;
};

export function useDashboardResource<T>(opts: {
  key: string;
  enabled: boolean;
  fetcher: DashboardFetcher<T>;
  staleTimeMs?: number;
  refreshIntervalMs?: number;
  /** Eligibility can change in another tab even while this cache is fresh.
   * Throttled to one refetch per FOCUS_REFRESH_MIN_MS. */
  refreshOnFocus?: boolean;
}): DashboardResource<T> {
  const { key, enabled, staleTimeMs = DEFAULT_STALE_MS, refreshIntervalMs = 0, refreshOnFocus = false } = opts;
  const fetcher = useRef(opts.fetcher);
  fetcher.current = opts.fetcher;
  const { refreshToken, beginRefreshWork, endRefreshWork } = useDashboardRefresh();
  const seenToken = useRef(refreshToken);
  const subscribe = useCallback((listener: () => void) => {
    const set = listeners.get(key) ?? new Set<() => void>();
    listeners.set(key, set); set.add(listener);
    return () => {
      set.delete(listener);
      queueMicrotask(() => {
        if (set.size) return;
        listeners.delete(key);
        const entry = cache.get(key);
        if (entry?.pending) {
          entry.controller?.abort(); entry.version++; entry.pending = undefined;
          entry.snapshot = { ...entry.snapshot, fetching: false };
        }
      });
    };
  }, [key]);
  const snapshot = useSyncExternalStore(subscribe, useCallback(() => cache.get(key)?.snapshot ?? EMPTY, [key]), () => EMPTY) as Snapshot<T>;
  const refresh = useCallback(async () => {
    if (!enabled) return;
    const entry = entryFor(key);
    // An explicit refresh is a new read boundary, not a promise for a read
    // that may have started before the caller's mutation committed.
    entry.version++;
    entry.controller?.abort(); entry.pending = undefined;
    entry.terminal = false; entry.retryAt = 0;
    await loadResource(key, (signal, context) => fetcher.current(signal, context), "refresh");
  }, [key, enabled]);

  useEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;
    const maybeLoad = (onFocus = false) => {
      window.clearTimeout(timer);
      if (document.visibilityState === "hidden") return;
      const entry = entryFor(key);
      if (entry.terminal || entry.pending) return;
      const age = Date.now() - entry.snapshot.fetchedAt;
      if (entry.retryAt <= Date.now() && (entry.snapshot.error || entry.snapshot.data === undefined || age >= staleTimeMs || entry.snapshot.fetchedAt === 0
        || (onFocus && refreshOnFocus && age >= FOCUS_REFRESH_MIN_MS))) {
        void loadResource(key, (signal, context) => fetcher.current(signal, context));
        return; // completion publishes, causing this effect to schedule again
      }
      // A timer that wakes while the data is still fresh must re-arm. Errors
      // retry independently of staleTime, including failed manual refreshes.
      const delay = entry.snapshot.error
        ? Math.max(1, entry.retryAt - Date.now())
        : refreshIntervalMs > 0 ? Math.max(refreshIntervalMs, staleTimeMs - age) : 0;
      if (delay > 0) timer = window.setTimeout(() => maybeLoad(), delay);
    };
    maybeLoad();
    const visible = () => maybeLoad(true);
    // P-15: a reconnect retries a failed read now instead of waiting out the
    // exponential backoff it built up while offline (up to five minutes).
    const online = () => {
      const entry = entryFor(key);
      if (!entry.terminal && entry.snapshot.error) { entry.retryAt = 0; entry.failures = 0; }
      maybeLoad();
    };
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("online", online);
    return () => { document.removeEventListener("visibilitychange", visible); window.removeEventListener("online", online); window.clearTimeout(timer); };
  }, [key, enabled, staleTimeMs, refreshIntervalMs, refreshOnFocus, snapshot.revision, snapshot.fetching, snapshot.error, snapshot.fetchedAt]);

  useEffect(() => {
    if (refreshToken === seenToken.current) return;
    seenToken.current = refreshToken;
    if (!enabled) return;
    beginRefreshWork();
    void refresh().finally(endRefreshWork);
  }, [refreshToken, enabled, refresh, beginRefreshWork, endRefreshWork]);
  return { data: enabled ? snapshot.data : undefined, loading: enabled && snapshot.data === undefined && !snapshot.error,
    refreshing: snapshot.fetching, error: enabled ? snapshot.error : null, refresh };
}

/**
 * Read-only view of a cached resource: never fetches, re-renders when the
 * cached snapshot changes. For chrome that summarises data a panel already
 * loads (the provider work-queue badge) without adding a request.
 */
export function usePeekDashboardResource<T>(key: string): { data: T | undefined; fetchedAt: number } {
  const subscribe = useCallback((listener: () => void) => {
    const set = listeners.get(key) ?? new Set<() => void>();
    listeners.set(key, set); set.add(listener);
    return () => { set.delete(listener); };
  }, [key]);
  const snapshot = useSyncExternalStore(subscribe, useCallback(() => cache.get(key)?.snapshot ?? EMPTY, [key]), () => EMPTY) as Snapshot<T>;
  return { data: snapshot.data, fetchedAt: snapshot.fetchedAt };
}
